/**
 * @file NonInventoryBadge.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-13
 * @modified 2026-09-13
 * @description Marks a record carrying at least one hand-typed line — one with
 *              a description but no inventory item behind it.
 *
 *              Worth flagging because such a line is invisible to everything
 *              keyed on an item: it has no cost of its own unless somebody typed
 *              one, it cannot be matched to price history, and it will not
 *              appear in any report that groups by product. None of that makes
 *              it wrong — a one-off haulage charge is a perfectly good line —
 *              but it does mean the record needs a human to have looked at it.
 *
 *              Follows ItemStatusBadge: a small span whose title names the
 *              reason, rather than a colour nobody can decode.
 */

import React from 'react';
import './NonInventoryBadge.css';

/**
 * @param {boolean} present  Whether the record has any non-inventory line.
 * @param {number?} count    How many, when known — worth saying if more than one.
 * @param {string}  variant  'badge' (default) or 'icon' for tight table cells.
 */
function NonInventoryBadge({ present, count = null, variant = 'badge', className = '' }) {
  if (!present) return null;

  const title = count && count > 1
    ? `${count} lines on this record were typed by hand and are not linked to an inventory item.`
    : 'A line on this record was typed by hand and is not linked to an inventory item.';

  if (variant === 'icon') {
    return (
      <span className={`non-inventory-badge is-icon ${className}`.trim()} title={title}>
        <span className="material-symbols-rounded">edit_note</span>
      </span>
    );
  }

  return (
    <span className={`non-inventory-badge ${className}`.trim()} title={title}>
      <span className="material-symbols-rounded">edit_note</span>
      Typed line{count && count > 1 ? ` ×${count}` : ''}
    </span>
  );
}

export default NonInventoryBadge;
